import * as React from "react";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { RouteComponentProps } from "@reach/router";
import { Field, FormikProps } from "formik";
import { cx, css } from "@emotion/css";
import { navigate } from "../support/hashRouting";
import { FormValues } from "../support/FormValues";
import FieldSelect from "../components/FieldSelect";
import FieldText from "../components/FieldText";
import { ClassName } from "../support/className";
import accountTypeOptions from "../support/accountTypeOptions";
import bootstrapStyles from "../css/bootstrap.module.css";
import formGroupStyles from "../css/formgroup.module.css";
import { StepHeader } from "../components/StepHeader";
import Breadcrumbs from "../components/Breadcrumbs";
import { commonFirmsNames } from "../support/commonFirmsNames";
import { Simple } from "./TransferDetailsOptions/Simple";
import { Sep } from "./TransferDetailsOptions/Sep";
import { Traditional } from "./TransferDetailsOptions/Traditional";
import { Rollover } from "./TransferDetailsOptions/Rollover";
import { OtherEmployer } from "./TransferDetailsOptions/OtherEmployer";
import { Roth } from "./TransferDetailsOptions/Roth";
import { RothPortionEnum } from "../support/RothPortionEnum";
import { AccountTypeEnum } from "../support/AccountTypeEnum";
import { GeneralButton } from "../components/GeneralButton";
import { FieldNameEnum } from "../support/FieldNameEnum";
import { OtherEmployerAccountType } from "../support/OtherEmployerAccountTypeArray";
import { useScrollTop } from "../customHooks/scrollTop";
import rttService from "../support/rtt-service.json";
import TurnstileLoader from "../libs/turnstile";
interface TransferDetailsProps extends RouteComponentProps {
  formikBag: FormikProps<FormValues>;
}
const stepHeaderText =
  "Provide a few details about the account you want to transfer.";
const stepNoteText = `Please refer to a statement from your current firm to complete this process.`;

const TransferDetails = ({
  formikBag: { values, errors, touched, setFieldValue },
  formikBag,
  location
}: TransferDetailsProps) => {
  useScrollTop(location);
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const [isTokenValid, setIsTokenValid] = useState<boolean>(false);
  const [clientIp, setClientIp] = useState<string>("");
  const [turnstileError, setTurnstileError] = useState<string>("");

  const turnstileSiteKey =
    process.env.NODE_ENV === "production"
      ? process.env.REACT_APP_TURNSTILE_SITEKEY_PROD ||
        "0x4AAAAAAEkM5WXs__is87vi"
      : process.env.REACT_APP_TURNSTILE_SITEKEY || "0x4AAAAAAEkMwF7moMH4rtLq";

  const turnstileVerifyUrl =
    process.env.NODE_ENV === "production"
      ? rttService.production.turnstileVerifyUrl
      : rttService.experimental.turnstileVerifyUrl;

  const potentialErrorFields = [
    FieldNameEnum.currentFirm,
    FieldNameEnum.currentAccount,
    FieldNameEnum.currentAccountType,
    FieldNameEnum.initialFundingDate,
    FieldNameEnum.rothYear,
    FieldNameEnum.toAccountType
  ];

  // Checks all errors in the entire applicaiton and compares them with the fields on the current page
  const pageValid = potentialErrorFields.every(field => !(field in errors));

  useEffect(() => {
    const fetchClientIp = async () => {
      try {
        const response = await fetch("https://api64.ipify.org?format=json");
        const data = await response.json();
        setClientIp(data.ip || "");
      } catch (error) {
        console.error("Error fetching client IP:", error);
        setClientIp("");
      }
    };

    fetchClientIp();
  }, []);

  const verifyTurnstileToken = async (token: string) => {
    try {
      const response = await axios.post(turnstileVerifyUrl, {
        token,
        remoteip: clientIp
      });
      if (response.status === 200) {
        setIsTokenValid(true);
        setTurnstileError("");
      } else {
        setIsTokenValid(false);
        setTurnstileError(
          "We were unable to verify that you are human. Please try again."
        );
        console.warn("Turnstile token is invalid.");
      }
    } catch (error) {
      console.error("Error verifying Turnstile token:", error);
      setIsTokenValid(false);
      setTurnstileError(
        "An error occurred while verifying the token. Please try again."
      );
    }
  };

  const getDefaultOptionForToAccountType = (
    currentAccount: AccountTypeEnum
  ): AccountTypeEnum | "" => {
    switch (currentAccount) {
      case AccountTypeEnum.SimpleIRA:
        return AccountTypeEnum.SimpleIRA;
      case AccountTypeEnum.SEPIRA:
        return AccountTypeEnum.SEPIRA;
      case AccountTypeEnum.SARSEPIRA:
        return AccountTypeEnum.SEPIRA;
      case AccountTypeEnum.TraditionalIRA:
        return AccountTypeEnum.TraditionalIRA;
      case AccountTypeEnum.Governmental457bPlan:
        return AccountTypeEnum.TraditionalIRA;
      case AccountTypeEnum.RolloverIRA:
        return AccountTypeEnum.TraditionalIRA;
      case AccountTypeEnum.Plan403b:
        return AccountTypeEnum.TraditionalIRA;
      case AccountTypeEnum.QualifiedRetirementPlan:
        return AccountTypeEnum.TraditionalIRA;
      case AccountTypeEnum.RothIRA:
        return AccountTypeEnum.RothIRA;
      default:
        return "";
    }
  };

  const onCurrentAccountChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const accountTypeWillSwitchTo = event.currentTarget
      .value as AccountTypeEnum;
    // set Default value for toAccountType
    const defaultOptionForAccountType = getDefaultOptionForToAccountType(
      accountTypeWillSwitchTo
    );
    // clear initialFundingDate field when do switching currentAccount
    setFieldValue(FieldNameEnum.initialFundingDate, "");
    setFieldValue(FieldNameEnum.toAccountType, defaultOptionForAccountType);
    setFieldValue(FieldNameEnum.rothYear, "");
    if (!OtherEmployerAccountType.includes(accountTypeWillSwitchTo)) {
      setFieldValue(FieldNameEnum.rothPortion, "");
    } else {
      setFieldValue(FieldNameEnum.rothPortion, RothPortionEnum.none);
    }
  };

  return (
    <div className={ClassName.transferDetailsWrapper}>
      <Breadcrumbs currentStep={1} />
      <div className={bootstrapStyles.row}>
        <div
          className={cx(
            bootstrapStyles["col-md-12"],
            formGroupStyles.contentPadding
          )}
        >
          <StepHeader stepHeader={stepHeaderText} stepNote={stepNoteText} />
          <p>All fields required unless otherwise noted.</p>
        </div>
      </div>
      <div className={cx(bootstrapStyles.row)}>
        <div className={bootstrapStyles["col-md-6"]}>
          <Field
            type="text"
            spellCheck="false"
            name={FieldNameEnum.currentFirm}
            label="Firm Name"
            isReadOnly={commonFirmsNames.some(
              ele => values.currentFirm === ele
            )}
            component={FieldText}
          />
        </div>
      </div>

      <div className={bootstrapStyles.row}>
        <div className={bootstrapStyles["col-md-6"]}>
          <Field
            type="text"
            spellCheck="false"
            name={FieldNameEnum.currentAccount}
            label="Account Number"
            component={FieldText}
          />
        </div>
      </div>
      <div className={cx(bootstrapStyles.row)}>
        <div className={bootstrapStyles["col-md-6"]}>
          <Field
            name={FieldNameEnum.currentAccountType}
            label="Account Type"
            options={accountTypeOptions}
            component={FieldSelect}
            onChange={onCurrentAccountChange}
            removeDefaultSelectOption={false}
          />
        </div>
      </div>
      {(() => {
        switch (values.currentAccountType) {
          case AccountTypeEnum.SimpleIRA:
            return <Simple formikBag={formikBag} />;
          case AccountTypeEnum.SEPIRA:
            return <Sep formikBag={formikBag} />;
          case AccountTypeEnum.SARSEPIRA:
            return <Sep formikBag={formikBag} />;
          case AccountTypeEnum.TraditionalIRA:
            return <Traditional formikBag={formikBag} />;
          case AccountTypeEnum.RolloverIRA:
            return <Rollover formikBag={formikBag} />;
          case AccountTypeEnum.QualifiedRetirementPlan:
            return <OtherEmployer formikBag={formikBag} />;
          case AccountTypeEnum.Plan403b:
            return <OtherEmployer formikBag={formikBag} />;
          case AccountTypeEnum.Governmental457bPlan:
            return <OtherEmployer formikBag={formikBag} />;
          case AccountTypeEnum.RothIRA:
            return <Roth formikBag={formikBag} />;

          default:
            return null;
        }
      })()}

      <TurnstileLoader
        siteKey={turnstileSiteKey}
        onSuccess={(token: string) => {
          setTurnstileToken(token);
          verifyTurnstileToken(token);
        }}
      />

      {turnstileError && <p className="error-message">{turnstileError}</p>}

      <div className={bootstrapStyles.row}>
        <div className={bootstrapStyles["col-md-12"]}>
          <hr />
        </div>
      </div>

      <div className={bootstrapStyles.row}>
        <div className={cx(bootstrapStyles["col-md-12"])}>
          <GeneralButton
            id="back"
            text="back"
            buttonType="secondary"
            onClick={() => {
              navigate("/");
            }}
          />
          <div
            className={css`
              float: right;
            `}
          >
            <GeneralButton
              id="continue"
              text="continue"
              onClick={() => {
                navigate("/transferInstructions");
              }}
              buttonType="primary"
              disabled={!pageValid || !isTokenValid}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransferDetails;
